import { useContext } from 'react'

import {
  faArrowRotateLeft, faBell, faBellOn, faCog, faDatabase, faFileChartColumn, faFloppyDisk, faReceipt, faShareAll,
} from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { encodeParams } from '@domain-group/javascript-utils'
import { IconWithMenu, Link } from '@domain-group/suit-theme'

import AuthenticatedDownload from '@components/AuthenticatedDownload'

import usePublicationOrder, { generateUrls } from '@hooks/usePublicationOrder'

import handleCallbackAction from '@functions/handleCallbackAction'

import { PublicationOrderModel } from '@models/types'
import PageContext from '@contexts/pageContext'
import ScopedConditionalContent from '@components/ScopedConditionalContent'

interface BrandGroupGroupActionsProps {
  publicationBookingId: number,
  publicationOrderId: number,
}

const openPublicPageUrl = (publicationOrder: PublicationOrderModel, public_token: string, booking_id: number) => {
  const params = encodeParams({ public_token, booking_id })
  const { publicUrl } = generateUrls(publicationOrder)

  window.open(`${publicUrl}?${params}`)
}

const generatePublicToken = (
  publicationOrder: PublicationOrderModel,
  booking_id: number,
  createPublicToken: () => Promise<object>,
) => {
  const { public_token } = publicationOrder

  if (public_token){
    openPublicPageUrl(publicationOrder, public_token, booking_id)
  } else {
    createPublicToken().then((result) => {
      // @ts-expect-error TS2339
      const { public_token: createdToken } = result.data

      openPublicPageUrl(publicationOrder, createdToken, booking_id)
    })
  }
}

const BrandGroupGroupActions = (props: BrandGroupGroupActionsProps) => {
  const { publicationOrderId, publicationBookingId } = props

  const pageContext = useContext(PageContext)
  const { reportingPayload } = pageContext

  const {
    callbacks: {
      // @ts-expect-error TS2339
      createPublicToken: createPublicTokenFn,
      revertPublicationOrderModal: revertModal,
      sendAgencyArtworkAlert: sendAgencyArtworkAlertFn,
      sendAgencyBookingAlert: sendAgencyBookingAlertFn,
    },
    publicationOrder,
    urls: {
      billingCsv, bookingForm, newscorpXml,
    },

  } = usePublicationOrder({ id: publicationOrderId })

  const {
    callbacks: { generateReport: generateFn },
    reportingForm: { setEntityState },
  } = reportingPayload

  const generateReportPayload = {
    callbacks: {
      action: generateFn,
    },
    toastText: 'Report Queued',
  }

  return (
    <IconWithMenu
      callbacks={{ onShowMenu: () => setEntityState({ publication_order_id: publicationOrderId }) }}
      icon={(
        <Link greyLink>
          <FontAwesomeIcon icon={faCog} />
        </Link>
      )}
      shouldUsePortal
    >

      {/*
       // @ts-expect-error TS2741 */}
      <IconWithMenu.Menu closeOnClick>
        <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support']}>
          <Link key="revert" onClick={() => revertModal()}>
            <FontAwesomeIcon icon={faArrowRotateLeft} /> Revert
          </Link>

          <AuthenticatedDownload key="bookingForm">
            <Link href={bookingForm}>
              <FontAwesomeIcon icon={faFloppyDisk} /> Booking Form
            </Link>
          </AuthenticatedDownload>

          <AuthenticatedDownload key="newscorpXml">
            <Link href={newscorpXml}>
              <FontAwesomeIcon icon={faDatabase} /> Newscorp XML
            </Link>
          </AuthenticatedDownload>

          <Link
            key="sendArtworkAlert"
            onClick={() => handleCallbackAction(
              { callbacks: { action: sendAgencyArtworkAlertFn }, toastText: 'Agency Artwork Sent' },
            )}
          >
            <FontAwesomeIcon icon={faBell} /> Send Artwork Alert
          </Link>

          <Link
            key="sendBookingAlert"
            onClick={() => handleCallbackAction(
              { callbacks: { action: sendAgencyBookingAlertFn }, toastText: 'Booking Alert Sent' },
            )}
          >
            <FontAwesomeIcon icon={faBellOn} /> Send Booking Alert
          </Link>
        </ScopedConditionalContent>

        <AuthenticatedDownload key="billing_csv">
          <Link href={billingCsv}>
            <FontAwesomeIcon icon={faReceipt} /> Billing CSV
          </Link>
        </AuthenticatedDownload>

        <Link
          key="artworkStatusReport"
          onClick={() => handleCallbackAction(generateReportPayload)}
        >
          <FontAwesomeIcon icon={faFileChartColumn} /> Artwork Status Report
        </Link>

        <Link
          key="sharePublicLink"
          onClick={() => generatePublicToken(publicationOrder, publicationBookingId, createPublicTokenFn)}
        >
          <FontAwesomeIcon icon={faShareAll} /> Share Public Link
        </Link>
      </IconWithMenu.Menu>
    </IconWithMenu>
  )
}

export default BrandGroupGroupActions
